import { LoadDataError, registerAppError } from 'src/utils/register-app-error';
import type { AnalyticsScriptPlacement, SourceInterface } from 'src/api';

type AnalyticsScriptConfig = Pick<
    SourceInterface,
    'analyticsScriptCode' | 'analyticsScriptContent' | 'analyticsScriptPlacement'
>;

const MARKER_ATTRIBUTE = 'data-vb-analytics';

// Внедрённые коды за время жизни страницы. Защищает от повторной вставки при
// двойном вызове componentDidMount в React.StrictMode и при SPA-навигации.
const processedCodes = new Set<string>();

// Внедряет сторонний скрипт аналитики, настроенный на источнике. Контент может
// содержать несколько узлов верхнего уровня (например, <script> + <noscript> для
// тег-менеджера). Любой сбой логируется и не прерывает работу приложения.
export function injectAnalyticsScript(source: AnalyticsScriptConfig | null | undefined): void {
    if (!source) {
        return;
    }

    const { analyticsScriptCode, analyticsScriptContent, analyticsScriptPlacement } = source;

    if (!analyticsScriptCode || !analyticsScriptContent) {
        return;
    }

    if (processedCodes.has(analyticsScriptCode)) {
        return;
    }

    // Помечаем код как обработанный до вставки: даже при сбое не повторяем
    // попытку и не дублируем лог ошибки на повторном рендере.
    processedCodes.add(analyticsScriptCode);

    try {
        const { target, prepend } = resolvePlacement(analyticsScriptPlacement);

        // createContextualFragment, в отличие от innerHTML, создаёт исполняемые
        // <script>-узлы, которые отрабатывают при вставке в документ.
        const fragment = document.createRange().createContextualFragment(analyticsScriptContent);

        Array.from(fragment.children).forEach((element) => {
            element.setAttribute(MARKER_ATTRIBUTE, analyticsScriptCode);
        });

        if (prepend) {
            target.insertBefore(fragment, target.firstChild);
        } else {
            target.appendChild(fragment);
        }
    } catch (e) {
        console.error(e);
        registerAppError(LoadDataError.FailedToInjectAnalyticsScript, e);
    }
}

function resolvePlacement(
    placement?: AnalyticsScriptPlacement
): { target: HTMLElement; prepend: boolean } {
    switch (placement) {
        case 'head_start':
            return { target: document.head, prepend: true };
        case 'head_end':
            return { target: document.head, prepend: false };
        case 'body_start':
            return { target: document.body, prepend: true };
        case 'body_end':
        default:
            return { target: document.body, prepend: false };
    }
}
